import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'hitmax_token';

const AuthContext = createContext(null);

const setAuthHeader = (token) => {
  if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  else delete axios.defaults.headers.common['Authorization'];
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    setAuthHeader(token);
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data.user))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setAuthHeader(null); });
  }, []);

  const persist = (data) => {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setAuthHeader(data.access_token);
    setUser(data.user);
  };

  const register = async ({ name, email, password }) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`,
        { name, email, password }, { withCredentials: true });
      persist(data);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, code: e.response?.data?.detail || 'ERROR' };
    }
  };

  const login = async ({ email, password }) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`,
        { email, password }, { withCredentials: true });
      persist(data);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, code: e.response?.data?.detail || 'ERROR' };
    }
  };

  const logout = () => {
    axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setAuthHeader(null);
    setUser(null);
  };

  const forgotPassword = async (email) => {
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, code: e.response?.data?.detail || 'ERROR' };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      return { ok: true };
    } catch (e) {
      return { ok: false, code: e.response?.data?.detail || 'ERROR' };
    }
  };

  return (
    <AuthContext.Provider value={{ user, register, login, logout, forgotPassword, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
